export type VoteLaneMode = 'TWITCH' | 'SPLIT' | 'YOUTUBE'

export const twitchChannelCountOf = (broadcasterUserIds: readonly string[] | null | undefined): number =>
  Array.isArray(broadcasterUserIds) ? broadcasterUserIds.length : 1

export const voteLaneModeOf = ({ twitchChannelCount, twitchTotal, youtubeTotal }: {
  twitchChannelCount: number
  twitchTotal: number
  youtubeTotal: number
}): VoteLaneMode => {
  if (!(youtubeTotal > 0)) return 'TWITCH'
  if (twitchTotal > 0 || twitchChannelCount > 0) return 'SPLIT'
  return 'YOUTUBE'
}

const cleanCount = (c: number): number => {
  if (typeof c !== 'number' || !Number.isFinite(c) || c <= 0) return 0
  return Math.min(Math.floor(c), Number.MAX_SAFE_INTEGER)
}

export const sumCounts = (counts: readonly number[]): number =>
  counts.reduce((s, c) => s + cleanCount(c), 0)

const SAFE_TOTAL = Math.floor(Number.MAX_SAFE_INTEGER / 101)

const floorDiv = (n: number, d: number): number => {
  let q = Math.floor(n / d)
  while (q > 0 && q * d > n) q -= 1
  while ((q + 1) * d <= n) q += 1
  return q
}

const floorsAndOrder = (clean: number[]): { floors: number[], order: number[] } | null => {
  const total = clean.reduce((s, c) => s + c, 0)
  if (total <= 0) return null
  const idx = clean.map((_, i) => i)
  if (total <= SAFE_TOTAL) {
    const floors = clean.map((c) => floorDiv(c * 100, total))
    const rems = clean.map((c, i) => c * 100 - floors[i] * total)
    return { floors, order: idx.sort((a, b) => (rems[b] !== rems[a] ? rems[b] - rems[a] : a - b)) }
  }
  const big = clean.map((c) => BigInt(c))
  const bigTotal = big.reduce((s, c) => s + c, BigInt(0))
  const hundred = BigInt(100)
  const bigFloors = big.map((c) => (c * hundred) / bigTotal)
  const rems = big.map((c, i) => c * hundred - bigFloors[i] * bigTotal)
  const byRem = (a: number, b: number): number => rems[b] > rems[a] ? 1 : rems[b] < rems[a] ? -1 : a - b
  return { floors: bigFloors.map((f) => Number(f)), order: idx.sort(byRem) }
}

export const largestRemainderPercents = (counts: readonly number[]): number[] => {
  const clean = counts.map(cleanCount)
  const plan = floorsAndOrder(clean)
  if (plan === null) return clean.map(() => 0)
  const out = plan.floors.slice()
  const deficit = 100 - out.reduce((s, f) => s + f, 0)
  for (let k = 0; k < deficit && k < plan.order.length; k += 1) out[plan.order[k]] += 1
  return out
}

export const topIndexes = (counts: readonly number[]): number[] => {
  const clean = counts.map(cleanCount)
  const max = clean.reduce((m, c) => (c > m ? c : m), 0)
  if (max <= 0) return []
  return clean.flatMap((c, i) => (c === max ? [i] : []))
}

export const splitWinnersOf = (
  twitchCounts: readonly number[],
  youtubeCounts: readonly number[]
): { agree: boolean, twitch: number[], youtube: number[] } => {
  const twitch = topIndexes(twitchCounts)
  const youtube = topIndexes(youtubeCounts)
  const agree = twitch.length > 0 &&
    twitch.length === youtube.length &&
    twitch.every((i, k) => youtube[k] === i)
  return { agree, twitch, youtube }
}
